import React, { useState, useEffect, useRef } from 'react';
import { Send, RotateCcw, X, Bot, CheckCheck } from 'lucide-react';

function FlowSimulator({ nodes = [], edges = [], onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [variables, setVariables] = useState({});
  const messagesEndRef = useRef(null);

  // 📍 Draggable Position State & Handlers
  const [position, setPosition] = useState({
    x: window.innerWidth - 365,
    y: 115
  });
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    dragOffsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    const newX = Math.max(10, Math.min(window.innerWidth - 360, e.clientX - dragOffsetRef.current.x));
    const newY = Math.max(70, Math.min(window.innerHeight - 560, e.clientY - dragOffsetRef.current.y));
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    startSimulator();
  }, []);

  const startSimulator = () => {
    setMessages([]);
    setVariables({});
    setInputValue('');
    setWaitingForInput(false);

    const startNode = nodes.find(n => n.id === 'start');
    if (!startNode) {
      if (nodes.length > 0) {
        executeNode(nodes[0].id, {});
      }
      return;
    }

    const firstEdge = edges.find(e => e.source === 'start');
    if (firstEdge) {
      executeNode(firstEdge.target, {});
    }
  };

  const formatText = (text, currentVars) => {
    if (!text) return '';
    let res = text;
    Object.entries(currentVars).forEach(([k, v]) => {
      res = res.replace(new RegExp(k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), v);
    });
    return res;
  };

  const executeNode = (nodeId, currentVars) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    setCurrentNodeId(nodeId);

    // 1. Send Message
    if (node.type === 'sendMessage') {
      const msgText = formatText(node.data.message || '', currentVars);
      setMessages(prev => [...prev, { sender: 'bot', text: msgText, type: 'text', time: getCurrentTime() }]);
      proceedToNext(nodeId, null, currentVars);
    }
    // 2. Interactive Message (Buttons)
    else if (node.type === 'interactiveMessage') {
      const msgText = formatText(node.data.text || '', currentVars);
      const rawBtns = node.data.buttons || '';
      const buttonList = rawBtns.split(',').map(b => b.trim()).filter(Boolean);

      setMessages(prev => [...prev, {
        sender: 'bot',
        text: msgText,
        buttons: buttonList,
        type: 'interactive',
        time: getCurrentTime()
      }]);

      setWaitingForInput(true);
    }
    // 3. Send Image
    else if (node.type === 'sendImage') {
      const imgUrl = node.data.localPreview || node.data.imageUrl;
      const caption = formatText(node.data.caption || '', currentVars);
      setMessages(prev => [...prev, {
        sender: 'bot',
        imageUrl: imgUrl,
        text: caption,
        type: 'image',
        time: getCurrentTime()
      }]);
      proceedToNext(nodeId, null, currentVars);
    }
    // 4. Wait for Reply
    else if (node.type === 'waitForReply') {
      setWaitingForInput(true);
    }
    // 5. Time based greeting
    else if (node.type === 'timeBasedGreeting') {
      const hour = new Date().getHours();
      let greeting = node.data.nightMsg || 'Hello!';
      if (hour >= 5 && hour < 12) greeting = node.data.morningMsg || 'Good morning! 🌅';
      else if (hour >= 12 && hour < 17) greeting = node.data.afternoonMsg || 'Good afternoon! ☀️';
      else if (hour >= 17 && hour < 22) greeting = node.data.eveningMsg || 'Good evening! 🌆';

      setMessages(prev => [...prev, { sender: 'bot', text: formatText(greeting, currentVars), type: 'text', time: getCurrentTime() }]);
      proceedToNext(nodeId, null, currentVars);
    }
    // Baaki nodes
    else {
      proceedToNext(nodeId, null, currentVars);
    }
  };

  const proceedToNext = (sourceId, sourceHandle, currentVars) => {
    let nextEdge = null;

    if (sourceHandle) {
      nextEdge = edges.find(e => e.source === sourceId && e.sourceHandle === sourceHandle);
    }

    if (!nextEdge) {
      nextEdge = edges.find(e => e.source === sourceId);
    }

    if (nextEdge) {
      setTimeout(() => {
        executeNode(nextEdge.target, currentVars);
      }, 500);
    }
  };

  const handleUserInput = (inputText) => {
    if (!inputText.trim()) return;

    const userMsg = inputText.trim();
    setMessages(prev => [...prev, { sender: 'user', text: userMsg, type: 'text', time: getCurrentTime() }]);
    setInputValue('');
    setWaitingForInput(false);

    const currentNode = nodes.find(n => n.id === currentNodeId);

    const updatedVars = {
      ...variables,
      '@input': userMsg,
      [currentNode?.data?.variable || '@input']: userMsg
    };
    setVariables(updatedVars);

    // 1. Current node checking
    if (currentNode && currentNode.type === 'waitForReply') {
      const isMatched = matchChoicesAndProceed(currentNode, userMsg, updatedVars);
      if (isMatched) return;
    }

    const nextEdge = edges.find(e => e.source === currentNodeId);
    if (nextEdge) {
      const nextNode = nodes.find(n => n.id === nextEdge.target);
      if (nextNode && nextNode.type === 'waitForReply') {
        setCurrentNodeId(nextNode.id);
        const isMatched = matchChoicesAndProceed(nextNode, userMsg, updatedVars);
        if (isMatched) return;
      }
    }

    // 2. Global Choice Jump
    const cleanUser = userMsg.toLowerCase().replace(/[^\w\s]/gi, '').trim();
    for (const n of nodes) {
      if (n.type === 'waitForReply' && Array.isArray(n.data.choices)) {
        const found = n.data.choices.find((c, idx) => {
          const cleanChoice = c.toLowerCase().replace(/[^\w\s]/gi, '').trim();
          return (
            cleanUser === cleanChoice ||
            cleanUser.includes(cleanChoice) ||
            cleanChoice.includes(cleanUser) ||
            userMsg.trim() === String(idx + 1)
          );
        });

        if (found) {
          const targetHandleId = `choice-${found.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
          const branchEdge = edges.find(e => e.source === n.id && e.sourceHandle === targetHandleId);
          if (branchEdge) {
            setCurrentNodeId(n.id);
            proceedToNext(n.id, targetHandleId, updatedVars);
            return;
          }
        }
      }
    }

    proceedToNext(currentNodeId, null, updatedVars);
  };

  const matchChoicesAndProceed = (targetNode, userMsg, currentVars) => {
    const choices = targetNode.data.choices || [];
    const cleanUser = userMsg.toLowerCase().replace(/[^\w\s]/gi, '').trim();

    const matchedChoice = choices.find((c, idx) => {
      const cleanChoice = c.toLowerCase().replace(/[^\w\s]/gi, '').trim();
      return (
        cleanUser === cleanChoice ||
        cleanUser.includes(cleanChoice) ||
        cleanChoice.includes(cleanUser) ||
        userMsg.trim() === String(idx + 1)
      );
    });

    if (!matchedChoice) return false;

    const handleId = `choice-${matchedChoice.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
    proceedToNext(targetNode.id, handleId, currentVars);
    return true;
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      zIndex: 9999,
      width: '340px',
      height: '540px',
      background: '#e2e8f0',
      borderRadius: '38px',
      padding: '8px 8px 10px',
      boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.35), 0 0 0 1px #cbd5e1',
      display: 'flex',
      flexDirection: 'column',
      userSelect: 'none',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* 📱 TOP HARDWARE: DRAGGABLE SPEAKER HANDLE */}
      <div 
        onMouseDown={handleMouseDown}
        title="Hold & Drag to move phone"
        style={{
          height: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          flexShrink: 0,
          cursor: 'grab'
        }}
      >
        {/* Speaker Grip Bar */}
        <div 
          style={{ 
            width: '56px', 
            height: '4px', 
            background: '#64748b', 
            borderRadius: '4px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transition: 'background 0.2s'
          }} 
          onMouseEnter={(e) => { e.currentTarget.style.background = '#0f172a'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#64748b'; }}
        />

        {/* Camera */}
        <div style={{ 
          width: '7px', 
          height: '7px', 
          borderRadius: '50%', 
          background: '#334155', 
          position: 'absolute', 
          right: '85px',
          pointerEvents: 'none'
        }} />
      </div>

      {/* 📱 INNER MOBILE SCREEN */}
      <div style={{
        flex: 1,
        background: '#efeae2',
        borderRadius: '26px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
      }}>
        {/* WhatsApp Top Header */}
        <div style={{
          background: '#075e54',
          padding: '8px 12px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#25d366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <Bot size={18} />
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', lineHeight: 1.1 }}>WhatsApp Preview</div>
              <div style={{ fontSize: '10px', color: '#bbf7d0' }}>Online • Test Mode</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={startSimulator}
              title="Restart Flow"
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '50%',
                color: 'white',
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <RotateCcw size={13} />
            </button>
            <button
              type="button"
              onClick={onClose}
              title="Close Preview"
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '50%',
                color: 'white',
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Chat Messages Body */}
        <div style={{
          flex: 1,
          padding: '10px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
          backgroundSize: '14px 14px'
        }}>
          {messages.map((m, idx) => (
            <div
              key={idx}
              style={{
                alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '86%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{
                background: m.sender === 'user' ? '#dcf8c6' : '#ffffff',
                padding: '7px 11px',
                borderRadius: m.sender === 'user' ? '10px 2px 10px 10px' : '2px 10px 10px 10px',
                boxShadow: '0 1px 1.5px rgba(0,0,0,0.1)',
                fontSize: '12px',
                color: '#1e293b',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {m.imageUrl && (
                  <img
                    src={m.imageUrl}
                    alt="preview"
                    style={{ width: '100%', maxHeight: '110px', objectFit: 'cover', borderRadius: '6px', marginBottom: '4px' }}
                  />
                )}

                {m.text}

                <div style={{
                  fontSize: '9px',
                  color: '#94a3b8',
                  textAlign: 'right',
                  marginTop: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '2px'
                }}>
                  {m.time}
                  {m.sender === 'user' && <CheckCheck size={11} color="#3b82f6" />}
                </div>
              </div>

              {/* Button Chips */}
              {m.buttons && m.buttons.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '5px' }}>
                  {m.buttons.map((btn, bIdx) => (
                    <button
                      key={bIdx}
                      type="button"
                      onClick={() => handleUserInput(btn)}
                      style={{
                        background: '#ffffff',
                        color: '#0284c7',
                        border: '1px solid #bae6fd',
                        borderRadius: '7px',
                        padding: '6px 10px',
                        fontSize: '11.5px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textAlign: 'center',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                    >
                      {btn}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Field */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUserInput(inputValue);
          }}
          style={{
            background: '#f0f2f5',
            padding: '6px 8px',
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            borderTop: '1px solid #e2e8f0'
          }}
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={waitingForInput ? 'Choose option or type...' : 'Waiting for bot...'}
            style={{
              flex: 1,
              padding: '7px 12px',
              borderRadius: '18px',
              border: '1px solid #cbd5e1',
              fontSize: '12px',
              outline: 'none',
              background: '#ffffff'
            }}
          />
          <button
            type="submit"
            disabled={!inputValue.trim()}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: inputValue.trim() ? '#075e54' : '#94a3b8',
              color: 'white',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: inputValue.trim() ? 'pointer' : 'default'
            }}
          >
            <Send size={13} />
          </button>
        </form>
      </div>

      {/* 📱 BOTTOM HOME BAR */}
      <div style={{
        height: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <div style={{ width: '90px', height: '3.5px', background: '#94a3b8', borderRadius: '3px' }} />
      </div>
    </div>
  );
}

export default FlowSimulator;